import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { BookabilityDecision, BookabilityUnmetCriteria } from "@src/appV2/OpenShifts/ShiftAction";
import { getBookabilityUnmetCriteriaMessage } from "@src/appV2/Shifts/utils/bookabilityUnmetCriteria";
import { isFuture } from "date-fns";

import { useShouldShowArrivalTimeModal } from "../../ArrivalTime/useShouldShowArrivalTimeModal";
import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import {
  SHIFT_DISCOVERY_SHIFT_ARRIVAL_TIME_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { getFormattedShiftPayBreakdownWithShiftAuction } from "../getFormattedShiftPayBreakdown";
import { PlaceBidButton } from "../ShiftAuction/PlaceBidButton";
import { useBidAction } from "../ShiftAuction/useBidAction";
import { BookingRequestButton } from "./BookingRequestButton";
import { BookShiftButton } from "./BookShiftButton";
import { FooterPayLabels } from "./FooterPayLabels";
import { RateNegotiationFooter } from "./RateNegotiationFooter";
import { useFooterData } from "./useFooterData";

interface ShiftBottomSheetFooterProps {
  shakeBookabilityCards?: () => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  minutesElapsed?: number;
}

// eslint-disable-next-line complexity
export function ShiftBottomSheetFooter(props: ShiftBottomSheetFooterProps) {
  const { shakeBookabilityCards, scrollContainerRef, minutesElapsed = 0 } = props;

  const {
    isBookable,
    isTestAccount,
    isRateNegotiationEnabled,
    shouldHideBookCta,
    shiftId,
    workplace,
    offer,
    navigateToModal,
    shift,
    isInstantBookDisabled,
    showBookingRequestButton,
    isShiftAlreadyBooked,
    missingBackgroundCheckDataTypes,
    hasExistingBookingRequest,
  } = useFooterData();

  const { bookabilityStatus, availableShiftBoost, computedArrivalData } =
    useShiftModalsDataContext();

  const shouldShowArrivalTimeModal = useShouldShowArrivalTimeModal({
    shiftStartIso: shift?.attributes.start,
    computedArrivalData,
    workplaceId: workplace?.id,
    workplaceMsa: workplace?.attributes?.location?.msa,
  });

  const { showErrorToast } = useToast();
  const bookabilityUnmetCriteria =
    bookabilityStatus?.attributes.bookability.decision === BookabilityDecision.BLOCKED
      ? bookabilityStatus?.attributes.bookability.unmetCriteria
      : [];

  const handleDisabledButtonClick = () => {
    if (bookabilityStatus?.attributes.bookability.decision === BookabilityDecision.BLOCKED) {
      // eslint-disable-next-line prefer-destructuring
      const firstUnmetCriteria = bookabilityUnmetCriteria[0];
      const message = isDefined(shift?.attributes.windowRestriction)
        ? "You don't have access yet. Check back later!"
        : getBookabilityUnmetCriteriaMessage(firstUnmetCriteria);

      scrollContainerRef?.current?.scrollTo({ top: 0, behavior: "smooth" });

      // Show shake animation for missing requirements or when no specific message, otherwise show toast
      if (
        firstUnmetCriteria === BookabilityUnmetCriteria.WORKER_MISSING_REQUIREMENTS ||
        !isDefined(message)
      ) {
        if (shakeBookabilityCards) {
          // wait for scroll-to-top to finish
          setTimeout(() => {
            shakeBookabilityCards();
          }, 500);
        }
      } else {
        showErrorToast(message);
      }
    }
  };

  const { attemptBidAction, isLoading: isBidActionLoading } = useBidAction({
    shiftId,
    workplaceId: workplace?.id,
    navigateToModal,
    onCanProceed: () => {
      if (!isDefined(shiftId)) {
        return;
      }

      if (shouldShowArrivalTimeModal) {
        navigateToModal(SHIFT_DISCOVERY_SHIFT_ARRIVAL_TIME_MODAL_PATH, {
          pathParams: { shiftId },
          searchParams: { redirectTo: "place-bid-modal" },
          clearExistingSearchParams: true,
        });
      } else {
        navigateToModal(SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH, {
          pathParams: { shiftId },
          clearExistingSearchParams: true,
        });
      }
    },
  });

  const handlePlaceBidButtonClick = (shiftId: string) => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_AUCTION_PLACE_BID_BUTTON_CLICKED, { shiftId });
    attemptBidAction();
  };

  const { shiftAuction, shiftAuctionBid } = shift ?? {};
  const canPlaceBid =
    isDefined(shiftAuction) &&
    bookabilityUnmetCriteria.length === 1 &&
    bookabilityUnmetCriteria[0] === BookabilityUnmetCriteria.SHIFT_AUCTION_ACTIVE &&
    isFuture(shiftAuction.attributes.endTime);

  const handleDisabledPlaceBidButtonClick = () => {
    if (isDefined(shiftAuctionBid)) {
      showErrorToast(
        "You've already placed a bid. You'll be notified whether you've won or not when the auction ends."
      );
    } else {
      handleDisabledButtonClick();
    }
  };

  const shiftAuctionPayBreakdown = shiftAuction
    ? getFormattedShiftPayBreakdownWithShiftAuction(shiftAuction.attributes)
    : undefined;

  const shouldShowRateNegotiation =
    isDefined(shift) &&
    isDefined(offer) &&
    isDefined(workplace?.id) &&
    !isDefined(shiftAuction) &&
    isBookable &&
    isRateNegotiationEnabled &&
    !hasExistingBookingRequest;

  if (shouldShowRateNegotiation) {
    return (
      <RateNegotiationFooter
        offer={offer}
        shift={shift}
        isShiftAlreadyBooked={isShiftAlreadyBooked}
        availableShiftBoost={availableShiftBoost}
        minutesElapsed={minutesElapsed}
        workplace={workplace}
        navigateToModal={navigateToModal}
        isTestAccount={isTestAccount}
        shouldHideBookCta={shouldHideBookCta}
        isInstantBookDisabled={isInstantBookDisabled}
        isBookable={isBookable}
        handleDisabledButtonClick={handleDisabledButtonClick}
        missingBackgroundCheckDataTypes={missingBackgroundCheckDataTypes}
      />
    );
  }

  return (
    <DialogFooter withShadow orientation="vertical">
      <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between" }}>
        <FooterPayLabels
          shiftAuctionPayBreakdown={shiftAuctionPayBreakdown}
          offer={offer}
          isShiftAlreadyBooked={isShiftAlreadyBooked}
          isInstantPay={shift?.attributes.isInstantPay}
          availableShiftBoost={availableShiftBoost}
          minutesElapsed={minutesElapsed}
        />

        {isTestAccount && (
          <Button disabled variant="contained" size="large">
            Test Account
          </Button>
        )}

        {showBookingRequestButton && isDefined(shiftId) && (
          <BookingRequestButton
            shiftId={shiftId}
            workplaceId={workplace?.id ?? ""}
            offerId={offer?.id}
            missingBackgroundCheckDataTypes={missingBackgroundCheckDataTypes}
            shiftIsAlreadyBooked={isShiftAlreadyBooked}
          />
        )}

        {!isTestAccount &&
          !shouldHideBookCta &&
          isDefined(shift) &&
          isDefined(offer) &&
          !isDefined(shiftAuction) && (
            <BookShiftButton
              isInstantBookDisabled={isInstantBookDisabled}
              disabled={!isBookable}
              shift={shift}
              missingBackgroundCheckDataTypes={missingBackgroundCheckDataTypes}
              rateNegotiationId={undefined}
              onBook={() => {
                navigateToModal(SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH, {
                  pathParams: { shiftId: shift.id },
                });
              }}
              onDisabledButtonClick={handleDisabledButtonClick}
            />
          )}
      </Stack>
      {isDefined(shift) && isDefined(shiftAuction) && (
        <Box sx={{ px: 5, width: "100%" }}>
          <PlaceBidButton
            fullWidth
            shiftId={shift.id}
            disabled={!canPlaceBid || isDefined(shiftAuctionBid)}
            isLoading={isBidActionLoading}
            placedBidAmountInMinorUnits={shiftAuctionBid?.attributes.bidAmount.amountInMinorUnits}
            onDisabledButtonClick={handleDisabledPlaceBidButtonClick}
            onPlaceBidButtonClick={handlePlaceBidButtonClick}
          />
        </Box>
      )}
    </DialogFooter>
  );
}
